/**
 * `src/platform/credential-mask.ts`: this module masks the credential shapes that `displayUntrusted` keeps
 * readable. It reads the whole server text before any cap (plan 2026-10-24-aws-dp-identity-and-lifecycle,
 * WS-14).
 *
 * Callers: `maskSatelliteReason` and `satelliteNameText` in `panes/dp-satellites-model.ts`, and the error
 * wrapper and the answer parse of `api/dpSatellites.api.ts`, which mask the body of every satellite
 * `ApiError` and the message of every refused answer before `apiErrorMessage` caps it. The satellite text
 * is server text with no other control for these shapes.
 *
 * Why the mask runs before the cap. `displayUntrusted` caps at 4000 code points first. A credential that
 * the cap cuts leaves a fragment shorter than the floor of every rule here, so a mask after the cap shows
 * that fragment (review rows WS-14-R7 and WS-14-R27). This module reads the whole text, so the cap that
 * follows it never splits a credential that a rule here matches.
 *
 * Every rule reads the visible view of `visible-text.ts`: the text with each invisible code point removed
 * (a format character, a control other than `\t`, `\n` and `\r`, a line or paragraph separator, a
 * blank-looking mark, and a `<U+XXXX>` escape token). So such a code point anywhere in a credential
 * cannot split it into parts below a floor (review rows WS-14-P2-R25, R28, R32 and R42). The mask
 * replaces a whole credential, the invisible code points inside it included, and keeps every other code
 * point.
 *
 * The shapes, all found on one view and replaced in one pass ({@link maskCredentialShapes}):
 *
 * 1. The self-named shapes of `redactSelfNamedCredentials`: a JWT, a Bearer token and the Google key
 *    shapes, each named by its prefix. `letterRuns: 'any'` reads them in the `'text'` context;
 *    `letterRuns: 'random'` in the `'name'` context. An invisible part in place of a space separates.
 * 2. URI userinfo (`https://user:password@host`): everything between `//` and the last `@` before the
 *    authority ends at `/`, `?`, `#` or white space. An empty user, a token alone (`https://<token>@host`)
 *    and a password that holds `@` go whole. A password that holds a raw `/` goes too, up to the last `@`
 *    before white space, `?` or `#` ({@link slashPasswordRanges}, review row WS-14-P3-R44).
 * 3. A random run of the AWS secret access key alphabet ({@link isSecretKeyRun}): a run of `[A-Za-z0-9/+]`,
 *    with optional `=` padding, that holds 40 characters in a row with a digit, `/` or `+`, in which each
 *    letter case holds a quarter of the letters or more, with at least 3.0 bits of entropy per character.
 *    The rule judges each window of 40 characters, so a key inside a longer run of other characters still
 *    goes (review rows WS-14-P3-R55 and R58). A key of letters and digits alone is one (review row
 *    WS-14-P2-R18), and so is a key right after `=` (`AWS_SECRET_ACCESS_KEY=<key>`, review rows WS-14-P2-R15
 *    and R37). A path of words, such as `source/MyApplication2026/configuration/secretKey`, has too few
 *    capitals and stays (review row WS-14-P2-R5).
 * 4. A token of `[A-Za-z0-9_-]`, read as segments between runs of `-` and `_` (so a leading, trailing or
 *    doubled separator changes nothing, review row WS-14-P3-R43), that is one of these (review rows
 *    WS-14-R29 to R31):
 *    - hex alone: 33 or more digits in one segment, above a trace id and a uuid without dashes (32), so a
 *      39-digit token goes (review row WS-14-P3-R48); 40 or more digits over joined segments (a uuid
 *      holds 32);
 *    - a token with one random segment: a segment of 33 or more hex digits; a segment of 40 or more
 *      letters and digits with a digit and at least 3.0 bits of entropy per character (review row
 *      WS-14-P3-R5); or a segment of 40 or more ASCII letters, which with `letterRuns: 'any'` always goes
 *      and with `letterRuns: 'random'` goes when a window of 40 of its letters holds each case at a quarter
 *      or more with at least 3.0 bits of entropy per letter. So `app-<key>` and `<key>_name` go (review row
 *      WS-14-P3-R49), and a name of one long word, camel-case words or one repeated letter stays;
 *    - segments joined by `-` or `_`, 40 or more characters long, with 8 or more digits outside any uuid in
 *      it: a lower-case joined credential, which P11 keeps as an identifier. A resource name such as
 *      `tcb-<dp>-sat-northamerica-northeast1` or `tcb-<uuid>-sat-us-east4` holds one or two such digits, so
 *      it stays.
 * 5. A run of 40 or more hexadecimal characters inside a longer token, and a GitHub token named by its
 *    prefix (`ghp_` and its kin with 36 characters, `github_pat_` with 22 or more).
 * 6. The value of a secret pair (`password=<value>`, P7 of `untrusted-text.ts`) and a PEM private key (P9),
 *    in both contexts, so a catalogue name that holds one, or a server text that the cap would cut inside
 *    one, shows none of it (review row WS-14-P3-R54).
 *
 * A second pass reads the joined view, with each line break removed too, so a credential that a server
 * wrapped over two lines goes whole (review row WS-14-P3-R50).
 *
 * Residual risks: a credential split by a space or a tab; a credential with no letter or digit mix that a
 * rule names; a joined credential shorter than 40 characters or with fewer than 8 digits; a URI password
 * longer than 512 characters.
 */
import { REDACTED_TEXT, privateKeyBlockRanges, secretPairRanges, selfNamedCredentialRanges } from './untrusted-text';
import { matchRanges, replaceVisibleRanges, type VisibleBoundaries, type VisibleRange } from './visible-text';

/**
 * URI userinfo of any shape: a scheme of at most 32 characters, `//`, then the userinfo (group 2) up to the
 * last `@` before the authority ends at `/`, `?`, `#` or white space.
 */
const USERINFO = /([a-z][a-z0-9+.-]{0,31}:\/\/)([^\s/?#]+)@/dgi;

/** The start of a URI userinfo with a password: a scheme, `//`, then the user (group 2) and `:`. */
const USERINFO_START = /([a-z][a-z0-9+.-]{0,31}:\/\/)([^\s/?#@:]*):/gi;

/** The farthest a URI password reaches past its `:`, in code units: a bound on the scan, far above any password. */
const MAX_URI_PASSWORD = 512;

/**
 * The userinfo of each URI on `visible` whose password holds a raw `/`, which ends the authority for
 * {@link USERINFO} (review row WS-14-P3-R44): from the user to the last `@` before white space, `?` or `#`,
 * within {@link MAX_URI_PASSWORD} code units of the `:`. A path with an `@` after a port
 * (`http://registry:4873/@scope/pkg`) reads as such a userinfo too, and goes: in satellite text a hidden
 * host costs less than a shown password. Linear: each start scans a bounded window. Pure.
 */
function slashPasswordRanges(visible: string): VisibleRange[] {
  const ranges: VisibleRange[] = [];
  for (const match of visible.matchAll(USERINFO_START)) {
    const userStart = match.index + String(match[1]).length;
    const passwordStart = match.index + match[0].length;
    const window = visible.slice(passwordStart, passwordStart + MAX_URI_PASSWORD);
    const end = window.search(/[\s?#]/);
    const password = end < 0 ? window : window.slice(0, end);
    const at = password.lastIndexOf('@');
    if (at > 0 && password.slice(0, at).includes('/')) ranges.push({ start: userStart, end: passwordStart + at });
  }
  return ranges;
}

/** A maximal run of `[A-Za-z0-9/+]` with optional `=` padding. `=` may precede it: `NAME=<key>`. */
const BASE64_RUN = /(?<![0-9A-Za-z/+])[0-9A-Za-z/+]+=*(?![0-9A-Za-z/+=])/g;

/** A maximal run of `[A-Za-z0-9_-]`. */
const TOKEN = /(?<![0-9A-Za-z_-])[0-9A-Za-z_-]+/g;

/**
 * A GitHub token, named by its prefix: `ghp_`, `gho_`, `ghu_`, `ghs_` or `ghr_` and 36 or more letters and
 * digits, or `github_pat_` and 22 or more of `[A-Za-z0-9_]`. Its body is shorter than every floor of rule 4
 * (review row WS-14-P3-R49).
 */
const GITHUB_TOKEN = /(?<![0-9A-Za-z])(?:gh[pousr]_[A-Za-z0-9]{36,}|github_pat_[A-Za-z0-9_]{22,})/g;

/** A run of 40 or more hexadecimal characters that no hexadecimal character precedes. */
const EMBEDDED_HEX = /(?<![0-9A-Fa-f])[0-9A-Fa-f]{40,}/g;

/** The length floor of every masked run but hex, in visible characters. 40 is above a uuid (36). */
const MIN_RUN = 40;

/** The length floor of one hex segment: above a trace id and a uuid without dashes (32). */
const MIN_HEX_SEGMENT = 33;

/** The digits outside a uuid that a joined token of mixed segments must hold before the mask treats it as a credential. */
const MIN_JOINED_DIGITS = 8;

/** A uuid, 8-4-4-4-12 hex in either case, wherever it sits. */
const UUID_ANYWHERE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** The entropy floor of a random run, in bits per character: P11's floor. */
const MIN_RANDOM_BITS = 3.0;

/** The options of {@link maskCredentialShapes}. */
export interface CredentialMaskOptions {
  /**
   * Which run of 40 or more ASCII letters to mask, and the context of the self-named shapes. `'any'` for
   * error text and step reasons, where no word reaches 40 letters, with the self-named shapes read in the
   * `'text'` context. `'random'` for a name: only a run that holds each letter case at a quarter of the
   * letters or more, with at least 3.0 bits of entropy per letter, whole or in a window of 40 letters, so a
   * name made of one long word,
   * camel-case words or one repeated letter stays whole (review row WS-14-R22); the self-named shapes
   * read in the `'name'` context.
   */
  readonly letterRuns: 'any' | 'random';
}

/** Shannon entropy of `run` in bits per character. */
function bitsPerCharacter(run: string): number {
  const counts = new Map<string, number>();
  for (const ch of run) counts.set(ch, (counts.get(ch) ?? 0) + 1);
  let bits = 0;
  for (const count of counts.values()) bits -= (count / run.length) * Math.log2(count / run.length);
  return bits;
}

/**
 * `true` when `run`, or some window of {@link MIN_RUN} characters of it, looks random: each letter case
 * holds a quarter of its letters or more, it has at least {@link MIN_RANDOM_BITS} bits of entropy per
 * character, and, with `needsMark`, it holds a digit, `/` or `+`. The whole run is tried first; the windows
 * then slide by one with running counts, so the cost is linear in the run's length times the distinct
 * characters of a window (at most 40).
 */
function randomWindow(run: string, needsMark: boolean): boolean {
  if (run.length < MIN_RUN) return false;
  const counts = new Map<number, number>();
  let upper = 0;
  let letters = 0;
  let marks = 0;
  const move = (unit: number, step: 1 | -1): void => {
    const next = (counts.get(unit) ?? 0) + step;
    if (next === 0) counts.delete(unit);
    else counts.set(unit, next);
    if (unit >= 0x41 && unit <= 0x5a) upper += step;
    if ((unit >= 0x41 && unit <= 0x5a) || (unit >= 0x61 && unit <= 0x7a)) letters += step;
    else marks += step;
  };
  const passes = (length: number): boolean => {
    if (needsMark && marks === 0) return false;
    if (upper < letters / 4 || letters - upper < letters / 4) return false;
    let bits = 0;
    for (const count of counts.values()) bits -= (count / length) * Math.log2(count / length);
    return bits >= MIN_RANDOM_BITS;
  };
  for (let index = 0; index < run.length; index += 1) move(run.charCodeAt(index), 1);
  if (passes(run.length)) return true;
  counts.clear();
  upper = 0;
  letters = 0;
  marks = 0;
  for (let index = 0; index < run.length; index += 1) {
    move(run.charCodeAt(index), 1);
    if (index >= MIN_RUN) move(run.charCodeAt(index - MIN_RUN), -1);
    if (index >= MIN_RUN - 1 && passes(MIN_RUN)) return true;
  }
  return false;
}

/** `true` when the `[A-Za-z0-9/+]` run `run` (padding included) holds the shape of an AWS secret access key. */
function isSecretKeyRun(run: string): boolean {
  return randomWindow(run.replace(/=+$/, ''), true);
}

/** `true` when the segment `segment` of `[A-Za-z0-9]` makes its token a credential by itself (rule 4). */
function isRandomSegment(segment: string, options: CredentialMaskOptions): boolean {
  if (/^[0-9A-Fa-f]+$/.test(segment)) return segment.length >= MIN_HEX_SEGMENT;
  if (segment.length < MIN_RUN) return false;
  if (/[0-9]/.test(segment)) return bitsPerCharacter(segment) >= MIN_RANDOM_BITS;
  return options.letterRuns === 'any' || randomWindow(segment, false);
}

/** `true` when the token `token` is a credential shape of rule 4. */
function isCredentialToken(token: string, options: CredentialMaskOptions): boolean {
  const segments = token.split(/[-_]+/).filter((segment) => segment !== '');
  const bare = segments.join('');
  if (segments.length > 1 && /^[0-9A-Fa-f]+$/.test(bare) && bare.length >= MIN_RUN) return true;
  if (segments.some((segment) => isRandomSegment(segment, options))) return true;
  return segments.length > 1 && token.length >= MIN_RUN && (token.replace(UUID_ANYWHERE, '').match(/[0-9]/g) ?? []).length >= MIN_JOINED_DIGITS;
}

/** The ranges of every shape of this module's header on the visible view `visible`. Pure. */
function credentialRanges(visible: string, boundaries: VisibleBoundaries, options: CredentialMaskOptions): VisibleRange[] {
  const keep = (pattern: RegExp, test: (run: string) => boolean): VisibleRange[] =>
    matchRanges(visible, pattern).filter((range) => test(visible.slice(range.start, range.end)));
  return [
    ...selfNamedCredentialRanges(visible, options.letterRuns === 'any' ? 'text' : 'name', boundaries),
    ...matchRanges(visible, USERINFO, 2),
    ...slashPasswordRanges(visible),
    ...keep(BASE64_RUN, isSecretKeyRun),
    ...keep(TOKEN, (run) => isCredentialToken(run, options)),
    ...matchRanges(visible, EMBEDDED_HEX),
    ...matchRanges(visible, GITHUB_TOKEN),
    ...secretPairRanges(visible),
    ...privateKeyBlockRanges(visible),
  ];
}

/**
 * `text` with each credential shape of this module's header replaced by `REDACTED_TEXT`.
 *
 * Input: any string, the whole server text before any cap. Output: the same string with each match
 * replaced; two shapes that overlap become one `REDACTED_TEXT`. A uuid, a machine type, a region, a
 * resource name with one or two digits and an English sentence stay. No cap, no layout and no escape:
 * the caller passes the result to `displayUntrusted` or `displayUntrustedName`. Pure, linear in the
 * length of `text` (each window test reads 40 characters), and a second pass returns its input.
 */
export function maskCredentialShapes(text: string, options: CredentialMaskOptions): string {
  const find = (visible: string, boundaries: VisibleBoundaries): VisibleRange[] => credentialRanges(visible, boundaries, options);
  const once = replaceVisibleRanges(text, find, REDACTED_TEXT);
  /* The joined view differs from the visible view only where a line break stands. */
  return /[\n\r]/.test(once) ? replaceVisibleRanges(once, find, REDACTED_TEXT, { joinLines: true }) : once;
}
